/* Quote Request Form Handler — PPW
 * Klaviyo JS SDK identify + Client Subscriptions API + Client Events API + GTM dataLayer
 *
 * Three mechanisms fire on every submission:
 *   0. _learnq identify     — creates/updates profile with phone immediately (new AND existing profiles)
 *   1. Client Subscriptions API — adds profile to list; saves phone for new subscribers
 *   2. Client Events API        — fires the named metric to trigger the flow (always fires)
 *
 * Works for new AND existing Klaviyo subscribers — identified by email,
 * no cookie dependency.
 *
 * Handles every .inquiry-form on the page automatically — no per-product
 * wiring needed, since snippets/quote-request-popup.liquid gives each
 * form/container/thank-you triplet matching IDs:
 *   form:       quote-form-{product.id}
 *   container:  quote-form-container-{product.id}
 *   thank-you:  quote-thank-you-{product.id}
 *
 * Upload to: Shopify Admin → Themes → Edit code → Assets
 * Add to page template (main-product.liquid, once per page):
 *   <script src="{{ 'quote-request-klaviyo-submission.js' | asset_url }}" defer></script>
 */
(function () {

  /* ─── ACCOUNT CONFIGURATION ───────────────────────────────────────────────
   * These are the only values that change between Klaviyo / GTM accounts.
   * ───────────────────────────────────────────────────────────────────────── */
  var CONFIG = {
    // Klaviyo public Company ID
    // Where to find: Klaviyo Admin → Settings → Account → API Keys → Public API Key (6-character code)
    klaviyoCompanyId: 'VcCjVx',

    // Klaviyo list ID for quote-request leads
    // Where to find: Klaviyo Admin → Lists → your list → ID shown in the URL
    klaviyoListId: 'S7ck9x',

    // The metric name that triggers your Klaviyo flow.
    // Must match EXACTLY — Klaviyo is case-sensitive.
    // Where to find: Klaviyo Admin → Flows → your flow → trigger metric name
    klaviyoEventName: 'Quote Request Submitted',

    // GTM dataLayer event name.
    // Must match the "Event Name" field in your GTM Custom Event trigger.
    // Where to find: GTM → Triggers → your trigger → Event Name
    gtmEventName: 'Product Enquiry Form Submit',
    // gtmEventName: 'quote_request_submit',
  };

  /* ───────────────────────────────────────────────────────────────────────── */

  var LOG = '[quote-request-form]';
  var FORM_ID_PREFIX = 'quote-form-';

  function normalizePhone(phone) {
    var d = (phone || '').replace(/\D/g, '');
    if (!d) return null;                                   // empty — omit from payload
    if (d.length === 10)                 return '+1' + d;  // US 10-digit
    if (d.length === 11 && d[0] === '1') return '+' + d;  // US 11-digit with country code
    return '+' + d;                                        // international — best effort E.164
  }

  function splitName(full) {
    var p = (full || '').trim().split(/\s+/);
    return { first: p[0] || '', last: p.slice(1).join(' ') || '' };
  }

  async function sha256hex(str) {
    var buf = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode((str || '').trim().toLowerCase())
    );
    return Array.from(new Uint8Array(buf))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  function buildProfileAttrs(data) {
    var n     = splitName(data.name);
    var phone = normalizePhone(data.phone);
    var attrs = {
      email:      data.email,
      first_name: n.first,
      last_name:  n.last,
      properties: {
        product_title: data.productTitle,
        product_url:   data.productUrl,
        product_price: data.productPrice,
      },
    };
    if (phone) attrs.phone_number = phone;
    return attrs;
  }

  // The Subscriptions API needs an explicit consent block, or it will
  // create/update the profile without actually opting them into the list —
  // that's why profiles were showing up in Profiles search but not in the
  // list membership. SMS consent is deliberately NOT included here since
  // this form has no separate SMS opt-in checkbox; adding it would be
  // legally risky (TCPA) without the user explicitly consenting to texts.
  function buildSubscriptionAttrs(data) {
    var attrs = buildProfileAttrs(data);
    attrs.subscriptions = {
      email: {
        marketing: {
          consent: 'SUBSCRIBED',
        }
      }
    };
    return attrs;
  }

  async function subscribeToList(data) {
    var profileAttrs = buildSubscriptionAttrs(data);
    console.log(LOG, '1. Subscriptions API — payload profile attrs:', JSON.stringify(profileAttrs));
    var res = await fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: CONFIG.klaviyoListId } }
            }
          }
        }),
      }
    );
    console.log(LOG, '1. Subscriptions API — response status:', res.status, res.statusText);
    if (!res.ok) {
      var body = await res.text().catch(function () { return '(unreadable)'; });
      console.error(LOG, '1. Subscriptions API — error body:', body);
    }
    return res;
  }

  async function submitToKlaviyo(data) {
    var profileAttrs = buildProfileAttrs(data);
    console.log(LOG, '2. Events API — payload profile attrs:', JSON.stringify(profileAttrs));
    var res = await fetch(
      'https://a.klaviyo.com/client/events/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: {
                  type:       'metric',
                  attributes: { name: CONFIG.klaviyoEventName }
                }
              },
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              },
              properties: {
                message:       data.message,
                phone:         data.phone,
                product_title: data.productTitle,
                product_url:   data.productUrl,
                product_price: data.productPrice,
                source_url:    window.location.href,
              }
            }
          }
        }),
      }
    );
    console.log(LOG, '2. Events API — response status:', res.status, res.statusText);
    if (!res.ok) {
      var body = await res.text().catch(function () { return '(unreadable)'; });
      console.error(LOG, '2. Events API — error body:', body);
    }
    return res;
  }

  async function fireDataLayer(data) {
    var results = await Promise.allSettled([sha256hex(data.email), sha256hex(data.phone)]);
    var payload = {
      event:         CONFIG.gtmEventName,
      email:         data.email,
      phone:         data.phone,
      hashedEmail:   results[0].status === 'fulfilled' ? results[0].value : '',
      hashedPhone:   results[1].status === 'fulfilled' ? results[1].value : '',
      product_title: data.productTitle,
      product_url:   data.productUrl,
      product_price: data.productPrice,
      source_url:    window.location.href,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    console.log(LOG, '3. dataLayer push:', JSON.stringify(payload));
  }

  function initForm(form) {
    var productId    = form.id.slice(FORM_ID_PREFIX.length);
    var container     = document.getElementById('quote-form-container-' + productId);
    var thankYou      = document.getElementById('quote-thank-you-' + productId);
    if (!container || !thankYou) {
      console.warn(LOG, 'Missing container/thank-you for form:', form.id);
      return;
    }

    console.log(LOG, 'Form initialised:', form.id);

    var submitBtn = form.querySelector('[type="submit"]');
    var btnText   = submitBtn ? submitBtn.querySelector('.quote-btn-text') : null;

    function showThankYou() {
      container.style.display = 'none';
      thankYou.style.display  = 'flex';
      thankYou.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (submitBtn) submitBtn.disabled = true;
      if (btnText)   btnText.textContent = 'Sending…';

      var v = function (sel) {
        var el = form.querySelector(sel);
        return el ? el.value : '';
      };
      var data = {
        name:         v('[name="contact[name]"]'),
        email:        v('[name="contact[email]"]'),
        phone:        v('[name="contact[phone]"]'),
        message:      v('[name="contact[body]"]'),
        productTitle: form.getAttribute('data-product-title') || '',
        productUrl:   form.getAttribute('data-product-url') || '',
        productPrice: form.getAttribute('data-product-price') || '',
      };

      console.log(LOG, 'Form submitted —', form.id);
      console.log(LOG, 'Raw form data:', JSON.stringify(data));
      var normalisedPhone = normalizePhone(data.phone);
      console.log(LOG, 'Phone raw:', data.phone, '→ normalised:', normalisedPhone);

      // 0. Klaviyo JS SDK identify — updates phone on the profile immediately,
      //    works for new AND existing subscribers
      if (window._learnq) {
        var n = splitName(data.name);
        var identifyPayload = {
          '$email':      data.email,
          '$first_name': n.first,
          '$last_name':  n.last,
          product_title: data.productTitle,
        };
        if (normalisedPhone) identifyPayload['$phone_number'] = normalisedPhone;
        window._learnq.push(['identify', identifyPayload]);
        console.log(LOG, '0. _learnq identify — queued:', JSON.stringify(identifyPayload));
      } else {
        console.warn(LOG, '0. _learnq not found — skipping identify call');
      }

      // 1. Subscriptions API — adds to list and saves phone for new subscribers
      await subscribeToList(data);
      // 2. Events API + dataLayer in parallel — fires the flow trigger and GTM event
      await Promise.allSettled([submitToKlaviyo(data), fireDataLayer(data)]);

      console.log(LOG, 'All API calls complete — showing thank-you');
      showThankYou();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('form[id^="' + FORM_ID_PREFIX + '"]').forEach(initForm);
  });

})();